import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { fetchCollectionV2, fetchBySlug } from '../lib/cms'

export default function OrderPage() {
  const [searchParams] = useSearchParams()
  const initialProductSlug = searchParams.get('produkt') || ''

  const [allProducts, setAllProducts] = useState([])
  const [selectedProductSlug, setSelectedProductSlug] = useState(initialProductSlug)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [quantity, setQuantity] = useState(1)

  // Form states
  const [formData, setFormData] = useState({
    virksomhed: '',
    cvr: '',
    kontaktperson: '',
    email: '',
    telefon: '',
    adresse: '',
    postnr_by: '',
    bemaerkninger: '',
    website_url: '', // Anti-spam honeypot
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedSuccess, setSubmittedSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Load product list for selection
  useEffect(() => {
    async function loadProducts() {
      try {
        const data = await fetchCollectionV2('produkter', { limit: 60, sort: 'sortering', order: 'asc' })
        setAllProducts(data.items || [])
      } catch (err) {
        console.error('Failed to load products in order page:', err)
      }
    }
    loadProducts()
  }, [])

  // Resolve selected product details
  useEffect(() => {
    if (!selectedProductSlug && allProducts.length > 0) {
      setSelectedProductSlug(allProducts[0].slug?._ || allProducts[0].slug)
    }

    const found = allProducts.find((p) => (p.slug?._ || p.slug) === selectedProductSlug)
    if (found) {
      setSelectedProduct(found)
    } else if (selectedProductSlug) {
      fetchBySlug(selectedProductSlug).then((p) => {
        if (p) setSelectedProduct(p)
      })
    }
  }, [selectedProductSlug, allProducts])

  function handleInputChange(e) {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  function validate() {
    const errs = {}
    if (!formData.kontaktperson.trim()) {
      errs.kontaktperson = 'Navn på kontaktperson skal udfyldes'
    }
    if (!formData.email.trim()) {
      errs.email = 'E-mailadresse skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Indtast venligst en gyldig e-mailadresse'
    }
    if (!formData.telefon.trim()) {
      errs.telefon = 'Telefonnummer skal udfyldes'
    } else if (formData.telefon.replace(/\D/g, '').length < 8) {
      errs.telefon = 'Telefonnummer skal indeholde mindst 8 cifre'
    }
    if (!formData.virksomhed.trim()) {
      errs.virksomhed = 'Virksomhedsnavn eller kundenavn skal udfyldes'
    }
    if (!formData.adresse.trim()) {
      errs.adresse = 'Leverings-/faktureringsadresse skal udfyldes'
    }
    if (!formData.postnr_by.trim()) {
      errs.postnr_by = 'Postnummer og by skal udfyldes'
    }
    if (!selectedProductSlug) {
      errs.product = 'Vælg venligst et produkt'
    }
    if (quantity < 1) {
      errs.quantity = 'Mængde skal være mindst 1'
    }
    return errs
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitError('')

    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      const firstKey = Object.keys(validationErrors)[0]
      const el = document.getElementById(firstKey)
      if (el) el.focus()
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        website_url: formData.website_url, // Honeypot
        form_type: 'Produktbestilling / Fakturering',
        valgt_produkt_navn: selectedProduct?.produktnavn || selectedProductSlug,
        valgt_produkt_slug: selectedProductSlug,
        produktmaengde: quantity,
        virksomhed: formData.virksomhed,
        cvr_nummer: formData.cvr || 'Ikke angivet',
        kontaktperson: formData.kontaktperson,
        email: formData.email,
        telefon: formData.telefon,
        adresse: formData.adresse,
        postnummer_by: formData.postnr_by,
        bemaerkninger: formData.bemaerkninger,
        timestamp: new Date().toISOString(),
      }

      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        throw new Error(`Fejl ved indsendelse (${res.status})`)
      }

      setSubmittedSuccess(true)
    } catch (err) {
      console.error('Order submission error:', err)
      setSubmitError(
        'Der opstod en fejl ved afsendelse af bestillingen. Prøv venligst igen eller ring direkte til os på 21 43 81 49.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SEO
        title="Bestil produkt – Fakturaafregning – Krüger Wet Blaster ApS"
        description="Afgiv bestilling på Krüger Wet Blaster maskiner, tilbehør eller forbrugsstoffer til efterfølgende fakturering."
      />

      {/* Header */}
      <section className="bg-slate-900 text-white border-b border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">
              Bestil produkt
            </h1>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Udfyld formularen for at afgive en bestillingsforespørgsel. Vi gennemgår ordren, bekræfter leveringstidspunkt og fremsender en faktura direkte til virksomheden. Ingen online kortbetaling påkrævet.
            </p>
          </div>
        </div>
      </section>

      {/* Form & Overview Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {submittedSuccess ? (
          <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200/90 p-8 sm:p-12 shadow-sm text-center space-y-6">
            <div className="text-emerald-600 text-5xl mx-auto">
              <i className="fa-solid fa-circle-check" aria-hidden="true" />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-extrabold text-slate-900">
                Tak for din bestilling!
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
                Vi har modtaget din ordreforespørgsel på{' '}
                <strong className="text-slate-900">
                  {quantity}x {selectedProduct?.produktnavn || selectedProductSlug}
                </strong>
                . Vi kontakter dig snarest med ordrebekræftelse og faktura.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-left text-xs space-y-2 max-w-md mx-auto text-slate-700">
              <div className="font-semibold text-slate-900 border-b border-slate-200 pb-1.5 text-sm">
                Ordredetaljer
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Produkt:</span>
                <span className="font-medium">{selectedProduct?.produktnavn}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Antal:</span>
                <span className="font-medium">{quantity} stk.</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Virksomhed:</span>
                <span className="font-medium">{formData.virksomhed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Kontaktperson:</span>
                <span className="font-medium">{formData.kontaktperson}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Afregning:</span>
                <span className="font-medium text-sky-700">Faktura</span>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <Link
                to="/produkter"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors shadow"
              >
                <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                <span>Tilbage til produkter</span>
              </Link>

              <button
                type="button"
                onClick={() => {
                  setSubmittedSuccess(false)
                  setQuantity(1)
                }}
                className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium px-5 py-3 rounded-lg transition-colors"
              >
                <span>Afgiv en ny bestilling</span>
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            {/* Left Col: Order Fields (7 cols) */}
            <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200/90 shadow-sm space-y-6">
              <div>
                <h2 className="text-lg font-bold text-slate-900">1. Vælg produkt & antal</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Produktet er automatisk forvalgt, men du kan ændre det nedenfor.
                </p>
              </div>

              {/* Product selector */}
              <div className="space-y-1.5">
                <label htmlFor="productSelect" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                  Valgt produkt <span className="text-red-500">*</span>
                </label>
                <select
                  id="productSelect"
                  value={selectedProductSlug}
                  onChange={(e) => setSelectedProductSlug(e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 font-medium"
                >
                  {allProducts.map((prod) => (
                    <option key={prod.id || (prod.slug?._ || prod.slug)} value={prod.slug?._ || prod.slug}>
                      {prod.produktnavn}
                    </option>
                  ))}
                </select>
                {errors.product && <p className="text-xs text-red-600 mt-1">{errors.product}</p>}
              </div>

              {/* Quantity Selector */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                  Produktmængde <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center border border-slate-300 rounded-lg overflow-hidden bg-slate-50">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 flex items-center justify-center text-slate-600 hover:bg-slate-200 active:bg-slate-300 transition-colors cursor-pointer"
                      aria-label="Formindsk antal"
                    >
                      <i className="fa-solid fa-minus text-xs" aria-hidden="true" />
                    </button>
                    <span className="w-14 text-center font-bold font-mono text-sm text-slate-900">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-10 flex items-center justify-center text-slate-600 hover:bg-slate-200 active:bg-slate-300 transition-colors cursor-pointer"
                      aria-label="Forøg antal"
                    >
                      <i className="fa-solid fa-plus text-xs" aria-hidden="true" />
                    </button>
                  </div>
                  <span className="text-xs text-slate-500">stk.</span>
                </div>
                {errors.quantity && <p className="text-xs text-red-600 mt-1">{errors.quantity}</p>}
              </div>

              <div className="pt-4 border-t border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">2. Fakturerings- og virksomhedsoplysninger</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Oplysningerne benyttes til faktura og forsendelse.
                </p>
              </div>

              {/* Company & CVR */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="virksomhed" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    Virksomhedsnavn <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="virksomhed"
                    name="virksomhed"
                    value={formData.virksomhed}
                    onChange={handleInputChange}
                    placeholder="F.eks. Vejles Autoværksted ApS"
                    className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                      errors.virksomhed ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {errors.virksomhed && <p className="text-xs text-red-600">{errors.virksomhed}</p>}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="cvr" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    CVR-nummer
                  </label>
                  <input
                    type="text"
                    id="cvr"
                    name="cvr"
                    value={formData.cvr}
                    onChange={handleInputChange}
                    placeholder="8-cifret CVR"
                    className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0"
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-1.5">
                <label htmlFor="kontaktperson" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                  Kontaktperson / Att. <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="kontaktperson"
                  name="kontaktperson"
                  value={formData.kontaktperson}
                  onChange={handleInputChange}
                  placeholder="Fornavn og efternavn"
                  className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                    errors.kontaktperson ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                {errors.kontaktperson && <p className="text-xs text-red-600">{errors.kontaktperson}</p>}
              </div>

              {/* Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    E-mailadresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="faktura@firma.dk"
                    className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                      errors.email ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleInputChange}
                    placeholder="F.eks. 12 34 56 78"
                    className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                      errors.telefon ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {errors.telefon && <p className="text-xs text-red-600">{errors.telefon}</p>}
                </div>
              </div>

              {/* Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label htmlFor="adresse" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    Adresse <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="adresse"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleInputChange}
                    placeholder="Gadenavn og nummer"
                    className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                      errors.adresse ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {errors.adresse && <p className="text-xs text-red-600">{errors.adresse}</p>}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="postnr_by" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                    Postnummer & By <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="postnr_by"
                    name="postnr_by"
                    value={formData.postnr_by}
                    onChange={handleInputChange}
                    placeholder="F.eks. 6650 Brørup"
                    className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                      errors.postnr_by ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {errors.postnr_by && <p className="text-xs text-red-600">{errors.postnr_by}</p>}
                </div>
              </div>

              {/* Notes / Description */}
              <div className="space-y-1.5">
                <label htmlFor="bemaerkninger" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                  Bemærkninger / Beskrivelse
                </label>
                <textarea
                  id="bemaerkninger"
                  name="bemaerkninger"
                  rows={3}
                  value={formData.bemaerkninger}
                  onChange={handleInputChange}
                  placeholder="Eventuelle ønsker til levering, dysestørrelser eller andre specifikationer..."
                  className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0"
                />
              </div>

              {/* Anti-spam honeypot - sr-only and absolute */}
              <div className="sr-only absolute -left-[9999px]">
                <label htmlFor="website_url">Efterlad dette felt tomt</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {submitError && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
                  <i className="fa-solid fa-circle-exclamation text-base shrink-0" aria-hidden="true" />
                  <span>{submitError}</span>
                </div>
              )}

              {/* Submit CTA */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2.5 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 disabled:opacity-50 text-white font-bold text-base py-3.5 px-6 rounded-lg shadow-md transition-all cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                      <span>Sender bestilling...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane text-sm" aria-hidden="true" />
                      <span>Send bestillingsforespørgsel</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-slate-500 text-center mt-3">
                  <i className="fa-solid fa-shield-halved mr-1 text-slate-400" aria-hidden="true" />
                  Vi behandler dine data fortroligt. Ingen kortbetaling nødvendig.
                </p>
              </div>
            </div>

            {/* Right Col: Order Summary Card (5 cols) */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-sm sticky top-28 space-y-5">
                <h3 className="text-base font-bold text-slate-900 border-b border-slate-200 pb-3 flex items-center justify-between">
                  <span>Ordreoversigt</span>
                  <span className="text-xs font-mono font-normal text-slate-500">
                    B2B Faktura
                  </span>
                </h3>

                {selectedProduct ? (
                  <div className="space-y-4">
                    <div className="flex gap-4 items-center">
                      <div className="w-20 h-20 bg-slate-50 rounded-none border border-slate-200 p-2 flex items-center justify-center shrink-0">
                        {selectedProduct.produktbillede?.[0] ? (
                          <img
                            src={selectedProduct.produktbillede[0]}
                            alt={selectedProduct.produktnavn}
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <i className="fa-solid fa-box text-slate-300 text-2xl" aria-hidden="true" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-bold text-slate-900 leading-snug">
                          {selectedProduct.produktnavn}
                        </h4>
                        <div className="text-xs text-slate-500 mt-1">
                          Valgt mængde:{' '}
                          <span className="font-bold text-slate-800">{quantity} stk.</span>
                        </div>
                        <Link
                          to={`/produkt/${selectedProductSlug}`}
                          className="text-xs text-sky-600 hover:underline mt-1 inline-block"
                        >
                          Se produktside →
                        </Link>
                      </div>
                    </div>

                    <div className="border-t border-slate-200 pt-4 space-y-2 text-xs text-slate-600">
                      <div className="flex justify-between py-1 border-b border-slate-100">
                        <span>Betalingsmetode</span>
                        <span className="font-semibold text-slate-800">Faktura (netto)</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-slate-100">
                        <span>Levering / Afhentning</span>
                        <span className="font-semibold text-slate-800">Aftales v. ordrebekræftelse</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span>Dansk garanti</span>
                        <span className="font-semibold text-emerald-600">Inkluderet</span>
                      </div>
                    </div>

                    <div className="pt-2 text-xs text-slate-700 space-y-1">
                      <div className="font-bold text-slate-900">
                        Faktureringsproces
                      </div>
                      <p className="text-[11px] leading-relaxed text-slate-600">
                        Efter modtagelse af din bestilling fremsendes ordrebekræftelse med opgørelse og betalingsbetingelser.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-6 text-center text-slate-400 text-xs">
                    Vælg et produkt for at se opsummering
                  </div>
                )}

                <div className="pt-2 border-t border-slate-100 text-xs text-slate-500 space-y-1 text-center sm:text-left">
                  <div className="font-medium text-slate-700">Krüger Wet Blaster ApS</div>
                  <div>Erhvervsvej 1, 6650 Brørup · CVR: 41047216</div>
                  <div>
                    Tlf:{' '}
                    <a href="tel:+4521438149" className="text-sky-600 hover:underline font-semibold whitespace-nowrap">
                      21 43 81 49
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  )
}
